// Shared search-scope persistence. When the user picks a region, city, or
// "near me" on one search page (Wineries, Experiences, …), we want that scope
// to carry over when they navigate to another search page within the same
// session. URL params remain the primary source of truth — sessionStorage is
// only consulted when the URL has no scope params, and it gets written
// alongside any URL update so the two stay in sync.
//
// Map bounds are intentionally NOT shared: a user-panned viewport on Wineries
// has no useful translation on Experiences (which has no map). Bounds stay in
// the URL of their own page.

const STORAGE_KEY = 'wx.searchScope';

export type SharedScope = {
  region?: { slug: string };
  city?: { name: string; lat: number; lng: number };
  userLocation?: { lat: number; lng: number };
  // Bounds-derived center + radius. Set when the user has panned/zoomed to a
  // custom map area on a page with a map (Wineries) and we need to translate
  // that to a non-map page (Experiences). The radius is the approximate
  // half-diagonal of the bounds in meters, so the search covers roughly the
  // same area the user was looking at.
  area?: { lat: number; lng: number; radiusMeters: number };
};

// Approximate half-diagonal distance of a lat/lng box in meters. Treats
// latitude as ~111km/degree and longitude as 111km × cos(centerLat). Good
// enough for "what radius covers this viewport" — not used for any
// distance-precision purpose.
export const boundsToCenterAndRadius = (bounds: {
  ne: { lat: number; lng: number };
  sw: { lat: number; lng: number };
}): { lat: number; lng: number; radiusMeters: number } => {
  const lat = (bounds.ne.lat + bounds.sw.lat) / 2;
  const lng = (bounds.ne.lng + bounds.sw.lng) / 2;
  const dLat = (bounds.ne.lat - bounds.sw.lat) / 2;
  const dLng = (bounds.ne.lng - bounds.sw.lng) / 2;
  const metersLat = dLat * 111_000;
  const metersLng = dLng * 111_000 * Math.cos((lat * Math.PI) / 180);
  const radiusMeters = Math.sqrt(metersLat * metersLat + metersLng * metersLng);
  return { lat, lng, radiusMeters };
};

export const readSearchScope = (): SharedScope | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SharedScope;
    return parsed;
  } catch {
    return null;
  }
};

export const writeSearchScope = (scope: SharedScope | null): void => {
  try {
    if (!scope || (!scope.region && !scope.city && !scope.userLocation && !scope.area)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // sessionStorage can throw in private mode / quota — ignore.
  }
};

export const clearSearchScope = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
